import { useEffect, type ReactNode } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";

import { useShareInfo } from "../api/shares";
import { ThemeToggle } from "./ThemeToggle";

/**
 * Chrome for published shares (`/s/:shareId`). No authentication, no sidebar, no workspace
 * navigation — a visitor sees the workspace's name, the shared content, and nothing else.
 */

/** The share segment of a public path, or `""` when this is not one. */
export function shareIdFromPath(pathname: string): string {
  const match = /^\/s\/([^/]+)/.exec(pathname);
  return match?.[1] === undefined ? "" : decodeURIComponent(match[1]);
}

/** `?theme=light|dark` forces the colour scheme and removes the viewer's choice (§3.5). */
export function forcedTheme(search: string): "light" | "dark" | null {
  const value = new URLSearchParams(search).get("theme");
  return value === "light" || value === "dark" ? value : null;
}

/**
 * Crawlers are kept out unless the share says otherwise (§3.4). The server does not render
 * this page, so the instruction is a `robots` meta tag rather than an `X-Robots-Tag` header.
 */
function useRobotsTag(allowIndexing: boolean): void {
  useEffect(() => {
    if (allowIndexing) return;
    const tag = document.createElement("meta");
    tag.name = "robots";
    tag.content = "noindex";
    document.head.append(tag);
    return () => {
      tag.remove();
    };
  }, [allowIndexing]);
}

export function PublicLayout(): ReactNode {
  const location = useLocation();
  const shareId = shareIdFromPath(location.pathname);
  const { data } = useShareInfo(shareId);
  const share = data?.shares[0];
  const themeLocked = forcedTheme(location.search) !== null;

  useRobotsTag(share?.allowIndexing ?? false);

  const workspace = data?.team.name ?? "";
  const home = `/s/${encodeURIComponent(shareId)}`;

  return (
    <div className="min-h-screen bg-canvas text-ink">
      <header className="flex items-center justify-between border-b border-line px-6 py-3">
        <Link to={home} className="flex items-center gap-2 text-sm font-semibold">
          {data?.team.avatarUrl ? (
            <img src={data.team.avatarUrl} alt="" className="h-5 w-5 rounded" />
          ) : null}
          <span>{workspace === "" ? "Shared document" : workspace}</span>
        </Link>
        {themeLocked ? null : <ThemeToggle />}
      </header>
      <main className="mx-auto max-w-5xl px-6 py-10">
        <Outlet />
      </main>
    </div>
  );
}
